import {
	borderColor,
	borderRadius,
	borderStyle,
	borderWidth,
	marginSize,
	paddingSize,
} from '../../Utilities/utils/Theming';
import { focusRing } from '../../Themes/base/focusRing';
import { ISmartCustomProps, Size } from './types';
import { StyleObject } from 'src/Themes';

// Toggle base styles
export const ToggleBaseStyles: StyleObject = {
	alignItems: 'center',
	display: 'inline-flex',
	marginBottom: 0,
	position: 'relative' as any,
};

export const ToggleTrackBaseStyles = (props: ISmartCustomProps) => {
	const size = props?.toggleSize ? props.toggleSize : 'default';
	return {
		minHeight: `var(--epods--${size}-toggle-height)`,
		...borderRadius(`var(--epods--${size}-toggle-border-radius)`),
		paddingLeft: `var(--epods--${size}-toggle-padding)`,
		width: props.innerLabels
			? `var(--epods--${size}-toggle-width)`
			: `var(--epods--${size}-no-inner-label-toggle-width)`,
	};
};

export const ToggleCircleStyles = ({ $theme, $error, $checked, $isHovered }: any, size: Size = 'default') => {
	return {
		width: `var(--epods--${size}-toggle-circle-width)`,
		height: `var(--epods--${size}-toggle-circle-height)`,
		backgroundColor: $theme.toggle.toggleCircleBackground,
		...borderWidth($error && !$checked ? '1px' : '0'),
		...borderStyle('solid'),
		...borderColor($isHovered ? $theme.toggle.toggleTrackErrorHoverBorder : $theme.toggle.toggleTrackErrorBorder),
		...marginSize('0px !important', '0px !important', '0px !important', '0px !important'),
		boxShadow: 'none',
	};
};

// Toggle track default styles
export const ToggleTrack: any = (
	$theme: any,
	$isHovered: any,
	$error: any,
	$checked: any,
	$disabled: any,
	$isFocused: boolean,
) => {
	// Default
	let result: any = {
		backgroundColor: $theme.colors.toggleTrackFill,
		...borderWidth('1px'),
		...($isFocused ? focusRing : {}),
	};
	if ($isHovered) {
		result = { ...result, backgroundColor: $theme.toggle.toggleTrackHover };
	}

	// Checked
	if ($checked) {
		result = { ...result, backgroundColor: 'var(--epods--color-bg-primary)' };
		if ($isHovered) {
			result = { ...result, backgroundColor: 'var(--epods--color-bg-primary-hover)' };
		}
	}

	// Error
	if ($error) {
		result = {
			...result,
			...borderWidth('1px'),
			...borderStyle('solid'),
			...borderColor($theme.toggle.toggleTrackErrorBorder),
			// override focusRing color
			'--epods--color-bg-primary': $theme.toggle.toggleTrackErrorBorder,
		};

		if ($isHovered) {
			result = {
				...result,
				backgroundColor: $theme.toggle.toggleTrackErrorHover,
				...borderColor($theme.toggle.toggleTrackErrorHoverBorder),
			};
		}
		if ($checked) {
			result = {
				...result,
				...borderStyle('solid'),
				backgroundColor: $theme.toggle.toggleTrackErrorChecked,
			};

			if ($isHovered) {
				result = {
					...result,
					backgroundColor: $theme.toggle.toggleTrackErrorCheckedHover,
					...borderColor($theme.toggle.toggleTrackErrorCheckedHover),
				};
			}
		}
	}

	// Disabled
	if ($disabled) {
		result = {
			...result,
			backgroundColor: $theme.colors.toggleTrackFillDisabled,
		};
		if ($checked) {
			result = {
				...result,
				backgroundColor: $theme.toggle.toggleTrackDisabledChecked,
			};
		}
	}

	return result;
};

export const ToggleTrackInnerLabelStyles = ({ $checked }: any, props: ISmartCustomProps): StyleObject => {
	const size = props?.toggleSize ? props.toggleSize : 'default';
	return props.innerLabels
		? {
				position: 'relative',
				'::before': {
					position: 'absolute',
					left: `var(--epods--${size}-toggle-on-label-left-offset)`,
					top: '50%',
					transform: 'translateY(-50%)',
					// Note: what comes after "/" is the alternative text
					content: props.innerLabels && '"' + props.innerLabels[0] + '"',
					opacity: $checked ? 1 : 0,
					color: 'var(--epods--color-text-inverted)',
					transition: 'opacity 200ms ease-out',
					fontSize: `var(--epods--${size}-toggle-font-size)`,
				},
				'::after': {
					position: 'absolute',
					right: `var(--epods--${size}-toggle-off-label-right-offset)`,
					top: '50%',
					transform: 'translateY(-50%)',
					// Note: what comes after "/" is the alternative text
					content: props.innerLabels && '"' + props.innerLabels[1] + '"',
					opacity: $checked ? 0 : 1,
					color: 'var(--epods--color-text-inverted)',
					transition: 'opacity 200ms ease-out',
					fontSize: `var(--epods--${size}-toggle-font-size)`,
				},
		  }
		: {};
};
export const ToggleInnerLabelStyles = ({ $checked }: any, props: ISmartCustomProps): StyleObject => {
	const size = props?.toggleSize ? props.toggleSize : 'default';
	return props.innerLabels
		? {
				transform: $checked
					? `var(--epods--${size}-toggle-circle-on-offset)`
					: `var(--epods--${size}-toggle-circle-off-offset)`,
		  }
		: {
				transform: $checked
					? `var(--epods--${size}-no-inner-toggle-circle-on-offset)`
					: `var(--epods--${size}-no-inner-toggle-circle-off-offset)`,
		  };
};

export const ToggleLabelStyles = ({ $theme }: any): StyleObject => ({
	...marginSize('0', '0', '0', '0'),
	...paddingSize('0', '0', '0', '0'),
	lineHeight: $theme.radio.labelHeight, // TODO: Need to review the CSS framework, it shod be 24px when fontsize is 16px
	fontFamily: 'var(--epods--font-base)',
	fontSize: $theme.radio.labelSize,
	color: 'var(--epods--color-text-primary)',
});
